import React, { useState, useEffect } from 'react';
import { SECTIONS } from '../constants';
import { Responses } from '../types';

interface QuestionnaireProps {
  onComplete: (responses: Responses) => void;
  onBack: () => void;
}

const Questionnaire: React.FC<QuestionnaireProps> = ({ onComplete, onBack }) => {
  const [currentSectionIdx, setCurrentSectionIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number>>({});

  // Robust scrolling to top on every phase change
  useEffect(() => {
    window.parent.postMessage({ type: 'scrollToTop' }, '*');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentSectionIdx]);

  const section = SECTIONS[currentSectionIdx];
  const progress = ((currentSectionIdx + 1) / SECTIONS.length) * 100;
  const isCurrentSectionComplete = section.questions.every(q => answers[q.id]);

  const handleAnswer = (questionId: number, value: number) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const next = () => {
    if (currentSectionIdx < SECTIONS.length - 1) {
      setCurrentSectionIdx(prev => prev + 1);
    } else {
      const formatted: Responses = { A: [], B: [], C: [], D: [], E: [] };
      SECTIONS.forEach(sec => {
        formatted[sec.id as keyof Responses] = sec.questions.map(q => answers[q.id]);
      });
      onComplete(formatted);
    }
  };

  const prev = () => {
    if (currentSectionIdx > 0) setCurrentSectionIdx(prev => prev - 1);
    else onBack();
  };

  return (
    <div className="p-8 md:p-12">
      <div className="mb-12">
        <div className="flex justify-between items-end mb-4">
          <span className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">Phase {currentSectionIdx + 1} / 5</span>
          <span className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">{section.title}</span>
        </div>
        <div className="w-full bg-white/5 h-1.5 rounded-full overflow-hidden border border-white/5 shadow-inner">
          <div 
            className="bg-primary h-full transition-all duration-700 ease-in-out shadow-[0_0_15px_rgba(130,109,236,0.6)]" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      <h2 className="text-2xl font-black text-white mb-10 tracking-tight uppercase italic">{section.title}</h2>

      <div className="space-y-10 mb-12">
        {section.questions.map((q, idx) => (
          <div key={q.id} className="space-y-6">
            <p className="text-lg font-bold text-gray-200 leading-tight flex items-start">
              <span className="text-primary mr-3 opacity-50 font-black">{idx + 1}.</span> {q.text}
            </p>
            <div className="grid grid-cols-1 gap-4">
              {q.options.map(opt => (
                <label 
                  key={opt.value}
                  className={`
                    flex items-center p-5 rounded-2xl border-2 cursor-pointer transition-all duration-300
                    ${answers[q.id] === opt.value 
                      ? 'border-primary bg-primary/10 shadow-[0_0_20px_rgba(130,109,236,0.15)]' 
                      : 'border-white/5 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.05]'
                    }
                  `}
                >
                  <div className={`w-5 h-5 rounded-full border-2 mr-4 flex items-center justify-center transition-all ${answers[q.id] === opt.value ? 'border-primary bg-primary' : 'border-gray-600'}`}>
                    {answers[q.id] === opt.value && <div className="w-2 h-2 bg-white rounded-full"></div>}
                  </div>
                  <input
                    type="radio"
                    name={`q-${q.id}`}
                    className="hidden"
                    checked={answers[q.id] === opt.value}
                    onChange={() => handleAnswer(q.id, opt.value)}
                  />
                  <span className={`font-bold uppercase text-sm tracking-widest ${answers[q.id] === opt.value ? 'text-white' : 'text-gray-400'}`}>{opt.text}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mt-12 pt-8 border-t border-white/5">
        <button
          onClick={prev}
          className="flex-1 py-4 px-6 rounded-xl font-black text-gray-500 hover:text-gray-300 transition-all uppercase text-xs tracking-widest"
        >
          {currentSectionIdx === 0 ? 'Exit Audit' : 'Previous Phase'}
        </button>
        <button
          onClick={next}
          disabled={!isCurrentSectionComplete}
          className={`
            flex-1 py-5 px-6 rounded-xl font-black text-white uppercase text-sm tracking-[0.2em] transition-all shadow-xl
            ${isCurrentSectionComplete 
              ? 'bg-primary hover:bg-opacity-90 glow-btn transform hover:-translate-y-1' 
              : 'bg-white/5 text-gray-700 cursor-not-allowed'}
          `}
        >
          {currentSectionIdx === SECTIONS.length - 1 ? 'Generate Report' : 'Next Phase'}
        </button>
      </div>
    </div>
  );
};

export default Questionnaire;